import JWPlayer = jwplayer.JWPlayer;
import {jwplayer} from "../../../shims-vue";

interface IObjectKeys {
    [key: string]: string | number | undefined;
}

interface EventParams extends IObjectKeys {
    action: string
    name?: string
    media_id: string
    channel?: string
    cid: number
    pbid: number
    psid: string
    ref: string
    length?: number
    buffering_time?: number
    buffering_pos?: number
    play_time?: number
    from?: number
    to?: number
    msg?: string
}

interface EventData {
    buffering_time?: number
    buffering_pos?: number
    buffering_end?: number
    length?: number
    play_time?: number
    played_time?: number
    from?: number
    to?: number
    msg?: string
}

export default class PlaybackReportService {
    private readonly itemId: string;
    private readonly channelId: number;
    private readonly ref: string;
    private readonly interval: number;
    private sid = "";
    private startedAt = 0;
    private buffering_started = 0;
    private buffering_from_pos = 0;
    private state = "ready";
    private player: JWPlayer | null = null;
    private playbackId = 1;
    private heartbeat:number | null = null;
    private heartbeatTime = 0;
    private onErrorCb: ErrorCallback | null = null;
    private debug = false;


    constructor(itemId: string, channelId: number, ref: string, interval: number) {
        this.itemId = itemId;
        this.channelId = channelId;
        this.ref = ref;
        this.interval = interval;
    }

    onErrorResponse(callback: ErrorCallback){
        this.onErrorCb = callback
    }

    incrPb() {
        this.playbackId = this.playbackId + 1;
    }

    playback(){
        const eventParams: EventParams = {
            action: "P",
            media_id: this.itemId,
            cid: this.channelId,
            pbid: this.playbackId,
            psid: this.sid,
            ref: this.ref,
        }
        this.reportEvent(eventParams)
    }

    playbackEvent(name: string, data: EventData){
        const eventParams: EventParams = {
            action: "pb",
            media_id: this.itemId,
            cid: this.channelId,
            pbid: this.playbackId,
            psid: this.sid,
            ref: this.ref,
            name: name
        }
        Object.keys(data).forEach((key: string) => {
            eventParams[key as keyof EventParams] = data[key as keyof EventData];
        })
        this.reportEvent(eventParams)
    }

    reportEvent(data: EventParams){
        if (!this.player || this.player.getState() === 'idle') return

        const url = `/track/?${this.serialize(data)}`
        fetch(url, {
            method: "GET",
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'include',
            redirect: 'manual',
            headers: {
                "X-FPS-Action": data.action
            }
        })
            .then((result) => {
                if ([400, 405].includes(result.status)){
                    return result.json()
                }else if([200, 201].includes(result.status)){
                    return {
                        status: result.status,
                    }
                }

                return Promise.reject(result)
            })
            .then((response) => {
                if (response?.error && this.onErrorCb) {
                    this.onErrorCb(response.error)
                }
            })
            .catch((err) => {
                if (this.debug)
                    console.log("error", err);
            })
    }

    serialize(data: EventParams) {
        const str: string[] = [];
        Object.keys(data).forEach((key: string) => {
            if (typeof data[key] !== 'undefined') {
                const value = data[key as keyof EventParams]?.toString()
                if (value)
                    str.push(key + "=" + encodeURIComponent(value));
            }
        })

        return str.join("&");
    }

    startHB(){
        if (this.heartbeat || this.interval === 0) return

        this.heartbeatTime = new Date().getTime();
        this.heartbeat = window.setInterval(() => {
            if (!this.player) return

            const playedTime = Math.ceil((new Date().getTime() - this.heartbeatTime) / 1000)
            const playTime = Math.floor(this.player.getPosition())
            this.playbackEvent("Heartbeat", {played_time: playedTime, play_time: playTime})
        }, this.interval * 1000)
    }

    stopHB(){
        if (!this.heartbeat) return
        clearInterval(this.heartbeat)
        this.heartbeat = null;
    }

    attachPlayer(player: JWPlayer){
        this.player = player;

        player.on("play", (event) => {
            if (!this.player) return;

            if (this.state === 'ready'){
                this.incrPb()
                this.playback()
                this.playbackEvent("Play", {})
                this.startHB()
                this.state = 'playing';
            }else if (this.state === 'paused'){
                this.playbackEvent("Resume", {play_time: Math.floor(this.player.getPosition())})
                this.startHB()
                this.state = 'playing';
            }else if (event.oldstate === 'buffering' && this.buffering_started === 0){
                this.playbackEvent("Play", {})
                this.startHB()
                this.state = 'playing';
            }else if (event.oldstate === 'buffering' && this.buffering_started !== 0){
                const current = new Date().getTime();
                const buffering_time = (current - this.buffering_started)/1000;
                this.playbackEvent("Bufferfull", {
                    length: buffering_time,
                    buffering_end: current,
                    buffering_pos: this.buffering_from_pos
                })
                this.buffering_started = 0;
                this.state = 'playing';
            }
        });

        player.on("pause", () => {
            if (!this.player) return;
            this.playbackEvent("Pause", {play_time: Math.floor(this.player.getPosition())})
            this.stopHB()
            this.state = 'paused';
        });

        player.on("buffer", (event) => {
            if (!this.player) return;
            if (event.oldstate === 'idle'){
                this.incrPb()
                this.playback()
                this.playbackEvent("Initiate", {})
                this.state = 'initiated';
            }
            if (event.oldstate === 'playing' && event.reason === 'stalled'){
                this.buffering_started = new Date().getTime();
                this.buffering_from_pos = Math.round(this.player.getPosition());
                this.state = 'buffering';
            }
        });

        player.on("seek", (event) => {
            this.playbackEvent("Seek", {from: Math.floor(event.position), to: Math.floor(event.offset)})
        });

        player.on("remove", () => {
            this.stopHB()
        });

        player.on("complete", () => {
            if (!this.player) return;
            this.stopHB();
            this.playbackEvent("Stop", {play_time: Math.floor(this.player.getPosition())})
            this.state = 'completed';
        });

        player.on("error", (event) => {
            if (!this.player) return;
            this.playbackEvent("Error", {
                msg: event.message,
                play_time: Math.floor(this.player.getPosition())
            })
            this.state = 'error';
        })
    }

    remove(){
        this.stopHB()
    }
}
